/**
 * 018: structure copied from ./columns.tsx (`refundColumns`) — same column shapes (status badge,
 * right-aligned money, a moment, a view action), applied to `PaymentRow` for the FIN-05 list.
 */
import { createColumnHelper } from "@tanstack/react-table";
import { Badge } from "rizzui";
import { PiEyeDuotone as EyeIcon } from "react-icons/pi";
import { Link } from "react-router-dom";
import { moment, money, paymentStatusColor, readPayment, type PaymentRow } from "../../lib/services/finance";
import { RowActions } from "../../components/table/row-actions";
import { orderPath } from "../../lib/paths";

const columnHelper = createColumnHelper<PaymentRow>();

/** Order number and customer are Billing's own labels; neither ever falls back to an id. */
export const paymentColumns = [
  columnHelper.accessor("providerReference", {
    header: "Provider reference",
    size: 220,
    cell: ({ row }) => <span className="font-mono text-sm">{row.original.providerReference}</span>,
  }),
  columnHelper.accessor((payment) => payment.orderNumber ?? "", {
    id: "orderId",
    header: "Order",
    size: 160,
    cell: ({ row }) => (
      <Link to={orderPath(row.original.orderNumber || row.original.orderId)} className="font-mono text-sm text-primary hover:underline">
        {row.original.orderNumber ?? "—"}
      </Link>
    ),
  }),
  columnHelper.accessor((payment) => payment.customerName ?? "", {
    id: "customer",
    header: "Customer",
    size: 200,
    cell: ({ row }) => <span className="text-sm">{row.original.customerName ?? "Unknown customer"}</span>,
  }),
  columnHelper.accessor("status", {
    header: "Status",
    size: 160,
    cell: ({ row }) => (
      <Badge variant="flat" color={paymentStatusColor(row.original.status)} rounded="md">
        {readPayment(row.original.status).heading}
      </Badge>
    ),
  }),
  columnHelper.accessor((payment) => Number(payment.amount), {
    id: "amount",
    header: "Amount",
    size: 150,
    meta: { align: "right" },
    cell: ({ row }) => <span className="font-semibold tabular-nums text-gray-900">{money(row.original.amount)}</span>,
  }),
  columnHelper.accessor("initiatedAt", {
    header: "Initiated",
    size: 200,
    cell: ({ row }) => <span className="text-muted-foreground">{moment(row.original.initiatedAt)}</span>,
  }),
  columnHelper.accessor("resolvedAt", {
    header: "Resolved",
    size: 200,
    cell: ({ row }) => <span className="text-muted-foreground">{moment(row.original.resolvedAt)}</span>,
  }),
  // No delete: a payment is a financial record, same rule `refundColumns`' own note states.
  columnHelper.display({
    id: "actions",
    header: "",
    size: 90,
    cell: ({ row }) => (
      <RowActions
        name={row.original.providerReference}
        primary={{ label: "View payment", icon: EyeIcon, to: `/finance/transactions?paymentId=${row.original.paymentId}` }}
      />
    ),
  }),
];
